import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, RefreshCw, CheckCircle2, XCircle,
  Clock, AlertTriangle, Upload, Square, CheckSquare, Info, ShieldAlert, MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface MetaCredentials {
  id: string;
  restaurant_id: string;
  meta_app_id: string;
  access_token: string;
  catalog_id: string;
  is_active: boolean;
  whatsapp_catalog_id: string | null;
  whatsapp_business_id: string | null;
  whatsapp_catalog_enabled: boolean;
  token_expires_at: string | null;
  last_sync_at: string | null;
}

interface LocalProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  is_available: boolean;
  sync_status?: string;
  wa_product_id?: string;
  error_message?: string;
}

const WhatsAppCatalogIntegration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<MetaCredentials | null>(null);
  const [products, setProducts] = useState<LocalProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());

  const [formWaCatalogId, setFormWaCatalogId] = useState("");
  const [formWaBusinessId, setFormWaBusinessId] = useState("");
  const [formWaEnabled, setFormWaEnabled] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }

      const { data: restaurant } = await supabase
        .from("restaurants")
        .select("id")
        .eq("owner_id", user.id)
        .single();

      if (!restaurant) { navigate("/dashboard"); return; }
      setRestaurantId(restaurant.id);

      const { data: creds } = await supabase
        .from("instagram_shop_credentials")
        .select("*")
        .eq("restaurant_id", restaurant.id)
        .maybeSingle();

      if (creds) {
        const c = creds as any;
        setCredentials({
          id: c.id,
          restaurant_id: c.restaurant_id,
          meta_app_id: c.meta_app_id,
          access_token: c.access_token,
          catalog_id: c.catalog_id,
          is_active: c.is_active,
          whatsapp_catalog_id: c.whatsapp_catalog_id || null,
          whatsapp_business_id: c.whatsapp_business_id || null,
          whatsapp_catalog_enabled: c.whatsapp_catalog_enabled || false,
          token_expires_at: c.token_expires_at,
          last_sync_at: c.last_sync_at,
        });
        setFormWaCatalogId(c.whatsapp_catalog_id || "");
        setFormWaBusinessId(c.whatsapp_business_id || "");
        setFormWaEnabled(c.whatsapp_catalog_enabled || false);
      }

      const { data: prods } = await supabase
        .from("products")
        .select("id, name, description, price, image_url, is_available")
        .eq("restaurant_id", restaurant.id)
        .eq("is_available", true)
        .order("name");

      const { data: mappings } = await supabase
        .from("whatsapp_catalog_products")
        .select("product_id, wa_product_id, sync_status, error_message")
        .eq("restaurant_id", restaurant.id);

      const mappingMap = new Map((mappings as any[] || []).map((m: any) => [m.product_id, m]));

      setProducts(
        (prods || []).map(p => ({
          ...p,
          sync_status: mappingMap.get(p.id)?.sync_status,
          wa_product_id: mappingMap.get(p.id)?.wa_product_id || undefined,
          error_message: mappingMap.get(p.id)?.error_message || undefined,
        }))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!restaurantId || !credentials) return;
    setSaving(true);
    try {
      await supabase
        .from("instagram_shop_credentials")
        .update({
          whatsapp_catalog_id: formWaCatalogId.trim() || null,
          whatsapp_business_id: formWaBusinessId.trim() || null,
          whatsapp_catalog_enabled: formWaEnabled,
        } as any)
        .eq("id", credentials.id);

      toast({ title: "Configurações do catálogo WhatsApp salvas!" });
      await loadData();
    } catch (err) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const syncProducts = async () => {
    if (!restaurantId || selectedProducts.size === 0) return;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-catalog-sync", {
        body: {
          action: "sync_products",
          restaurant_id: restaurantId,
          product_ids: Array.from(selectedProducts),
        },
      });

      if (error) throw error;

      toast({
        title: "Sincronização concluída",
        description: data?.message || "Produtos enviados para o catálogo WhatsApp",
      });
      setSelectedProducts(new Set());
      await loadData();
    } catch (err) {
      toast({ title: "Erro na sincronização", description: String(err), variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  const toggleProduct = (id: string) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const hasMetaCreds = !!credentials?.access_token && !!credentials?.catalog_id;

  const syncStatusIcon = (status?: string) => {
    switch (status) {
      case "synced": return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "error": return <XCircle className="h-4 w-4 text-destructive" />;
      case "pending": return <Clock className="h-4 w-4 text-amber-500" />;
      default: return <span className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <MessageCircle className="h-5 w-5 text-emerald-500" />
          <h1 className="text-lg font-semibold">Catálogo WhatsApp</h1>
          {credentials?.whatsapp_catalog_enabled && (
            <Badge className="bg-emerald-500/10 text-emerald-600 ml-auto">Ativo</Badge>
          )}
        </div>
      </header>

      <main className="container max-w-4xl py-6 space-y-6">
        {!hasMetaCreds && (
          <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="flex items-center gap-3 py-4">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Credenciais do Meta não configuradas
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Configure primeiro suas credenciais na página do{" "}
                  <button onClick={() => navigate("/instagram-shop")} className="underline font-medium">
                    Instagram Shop
                  </button>
                  . O catálogo WhatsApp usa as mesmas credenciais e catálogo do Meta.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="config">
          <TabsList className="w-full">
            <TabsTrigger value="config" className="flex-1">Configuração</TabsTrigger>
            <TabsTrigger value="catalog" className="flex-1">Catálogo</TabsTrigger>
            <TabsTrigger value="status" className="flex-1">Status</TabsTrigger>
          </TabsList>

          {/* CONFIG TAB */}
          <TabsContent value="config" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  Como funciona
                </CardTitle>
                <CardDescription>
                  Sincronize seus produtos com o catálogo do WhatsApp Business
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</span>
                    <p>O catálogo do WhatsApp Business usa a <strong>mesma API de Commerce do Meta</strong> usada pelo Instagram Shop. Suas credenciais já configuradas serão aproveitadas.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</span>
                    <p>Se quiser usar um <strong>catálogo diferente</strong> para o WhatsApp, informe o ID do catálogo abaixo. Caso contrário, será usado o mesmo catálogo do Instagram Shop.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">3</span>
                    <p>No <a href="https://business.facebook.com/settings/whatsapp-business-accounts" target="_blank" rel="noopener" className="text-primary underline">Meta Business Suite</a>, vincule o catálogo à sua conta do WhatsApp Business.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">4</span>
                    <p>Ative a integração abaixo e sincronize seus produtos — eles aparecerão no catálogo do seu WhatsApp Business automaticamente.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 mt-4">
                  <MessageCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    <strong>Dica:</strong> Se você já configurou o Instagram Shop, pode usar o mesmo catálogo! Basta ativar a integração e sincronizar.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Configurações do Catálogo WhatsApp</CardTitle>
                <CardDescription>
                  {hasMetaCreds
                    ? "Credenciais do Meta detectadas. Configure os campos opcionais abaixo."
                    : "Configure primeiro as credenciais do Meta na página Instagram Shop."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {hasMetaCreds && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 p-3 rounded-lg">
                    <CheckCircle2 className="h-4 w-4" />
                    Credenciais do Meta conectadas (Catalog ID: {credentials?.catalog_id?.slice(0, 8)}...)
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catalog ID do WhatsApp (opcional)</Label>
                    <Input
                      value={formWaCatalogId}
                      onChange={e => setFormWaCatalogId(e.target.value)}
                      placeholder="Deixe vazio para usar o mesmo do Instagram"
                      disabled={!hasMetaCreds}
                    />
                    <p className="text-xs text-muted-foreground">
                      Se não informado, usa o mesmo catálogo configurado no Instagram Shop.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>WhatsApp Business Account ID (opcional)</Label>
                    <Input
                      value={formWaBusinessId}
                      onChange={e => setFormWaBusinessId(e.target.value)}
                      placeholder="ID da conta WhatsApp Business"
                      disabled={!hasMetaCreds}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formWaEnabled}
                      onCheckedChange={setFormWaEnabled}
                      disabled={!hasMetaCreds}
                    />
                    <Label>Catálogo WhatsApp ativo</Label>
                  </div>
                  <Button onClick={saveSettings} disabled={saving || !hasMetaCreds}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Salvar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CATALOG TAB */}
          <TabsContent value="catalog" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Enviar Produtos</CardTitle>
                    <CardDescription>Selecione os produtos para sincronizar com o catálogo WhatsApp</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={toggleAll}>
                      {selectedProducts.size === products.length ? (
                        <><CheckSquare className="h-4 w-4 mr-1" /> Desmarcar</>
                      ) : (
                        <><Square className="h-4 w-4 mr-1" /> Todos</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={syncProducts}
                      disabled={syncing || selectedProducts.size === 0 || !credentials?.whatsapp_catalog_enabled}
                    >
                      {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                      Sincronizar ({selectedProducts.size})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!credentials?.whatsapp_catalog_enabled && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg mb-4">
                    <ShieldAlert className="h-4 w-4" />
                    Ative a integração na aba Configuração antes de sincronizar.
                  </div>
                )}

                <div className="space-y-2">
                  {products.map(product => (
                    <div
                      key={product.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedProducts.has(product.id) ? "bg-primary/5 border-primary/30" : "hover:bg-muted/50"
                      }`}
                      onClick={() => toggleProduct(product.id)}
                    >
                      <button className="shrink-0">
                        {selectedProducts.has(product.id) ? (
                          <CheckSquare className="h-5 w-5 text-primary" />
                        ) : (
                          <Square className="h-5 w-5 text-muted-foreground" />
                        )}
                      </button>

                      {product.image_url && (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="h-10 w-10 rounded object-cover"
                        />
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground">
                          R$ {product.price.toFixed(2)}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {syncStatusIcon(product.sync_status)}
                        {product.sync_status === "synced" && (
                          <Badge variant="outline" className="text-xs text-emerald-600">Sincronizado</Badge>
                        )}
                        {product.sync_status === "error" && (
                          <Badge variant="outline" className="text-xs text-destructive">Erro</Badge>
                        )}
                      </div>
                    </div>
                  ))}

                  {products.length === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      Nenhum produto disponível. Adicione produtos no cardápio primeiro.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* STATUS TAB */}
          <TabsContent value="status" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Status da Sincronização</CardTitle>
                    <CardDescription>Veja o status de cada produto sincronizado com o WhatsApp</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadData}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {products.filter(p => p.sync_status).map(product => (
                    <div key={product.id} className="flex items-center gap-3 p-3 rounded-lg border">
                      {syncStatusIcon(product.sync_status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        {product.error_message && (
                          <p className="text-xs text-destructive truncate">{product.error_message}</p>
                        )}
                        {product.wa_product_id && (
                          <p className="text-xs text-muted-foreground">ID: {product.wa_product_id}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={`text-xs ${
                        product.sync_status === "synced" ? "text-emerald-600" :
                        product.sync_status === "error" ? "text-destructive" : "text-amber-600"
                      }`}>
                        {product.sync_status === "synced" ? "Sincronizado" :
                         product.sync_status === "error" ? "Erro" : "Pendente"}
                      </Badge>
                    </div>
                  ))}

                  {products.filter(p => p.sync_status).length === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      Nenhum produto foi sincronizado ainda.
                    </p>
                  )}
                </div>

                {credentials?.last_sync_at && (
                  <p className="text-xs text-muted-foreground mt-4 text-center">
                    Última sincronização: {new Date(credentials.last_sync_at).toLocaleString("pt-BR")}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default WhatsAppCatalogIntegration;
